import { useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PratusSelect from 'components/DesignSystems/New/PratusSelect';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger,
  ComboboxValue,
  useComboboxAnchor,
} from '@/components/ui/combobox';
import { Field, FieldLabel } from '@/components/ui/field';
import {
  isLayerObjectSymbol,
  USCG_SYMBOLOGY_OPTIONS,
  type UscgSymbol,
} from './utils/uscgSymbology';

const SIDE_SYMBOLS = ['customPoint', 'customLine', 'customPolygon'] as const;

type SymbologyOptionType = {
  value: string;
  label: string;
  iconSrc?: string;
  swatch?: string;
};

function formatSymbologyOptionLabel(option: SymbologyOptionType) {
  return (
    <span className="d-flex align-items-center gap-2">
      {option.swatch ? (
        <span
          className="symbology-icon"
          style={{
            background: option.swatch,
            borderRadius: 999,
            display: 'inline-block',
          }}
        />
      ) : null}
      {option.iconSrc && (
        <img src={option.iconSrc} alt="" className="symbology-icon" />
      )}
      <span>{option.label}</span>
    </span>
  );
}

type FormSymbologySelectProps = {
  name?: string;
  excludeOptions?: UscgSymbol[];
  includeCustomPolygon?: boolean;
  onSymbologyChange?: (symbology: UscgSymbol | null) => void;
  required?: boolean;
  /**
   * Opt-in, because the legacy select is still what the action and resource
   * forms render. Only the converted Layers tab object form asks for shadcn.
   */
  variant?: 'legacy' | 'shadcn';
};

const FormSymbologySelect: React.FC<FormSymbologySelectProps> = ({
  name = 'symbology',
  excludeOptions = [],
  includeCustomPolygon = false,
  onSymbologyChange,
  variant = 'legacy',
  required = false,
}) => {
  const { control } = useFormContext();
  const symbolAnchor = useComboboxAnchor();
  const showSideSymbols = variant === 'shadcn' && includeCustomPolygon;
  const menuOpenRef = useRef(false);
  const blockTriggerOpenRef = useRef(false);
  const triggerId = `${name}-symbology`;

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!menuOpenRef.current) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('label')?.htmlFor === triggerId) {
        blockTriggerOpenRef.current = true;
      }
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [triggerId]);
  const options: SymbologyOptionType[] = useMemo(() => {
    const filtered = USCG_SYMBOLOGY_OPTIONS.filter(
      (opt) =>
        !excludeOptions.includes(opt.type) &&
        (includeCustomPolygon || !isLayerObjectSymbol(opt.type)) &&
        !(
          showSideSymbols &&
          SIDE_SYMBOLS.some((type) => type === opt.type)
        )
    ).map((opt) => ({
      value: opt.type,
      label: opt.label,
      iconSrc: opt.iconSrc,
      swatch: opt.swatch,
    }));

    return [{ value: '', label: 'None' }, ...filtered];
  }, [excludeOptions, includeCustomPolygon, showSideSymbols]);

  return (
    <div data-tour="hub-cop-symbology">
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value } }) => {
        const option = options.find((o) => o.value === value) ?? options[0];

        if (variant === 'shadcn') {
          const sideSelected = SIDE_SYMBOLS.some((type) => type === value);
          const choose = (next: UscgSymbol | '') => {
            if ((value || '') === next) return;
            onChange(next);
            onSymbologyChange?.((next || null) as UscgSymbol | null);
          };
          return (
            <Field>
              <FieldLabel htmlFor={`${name}-symbology`}>
                <span>
                  Select Symbol
                  {required ? (
                    <span className="tw:text-destructive" aria-hidden="true">
                      {' '}
                      *
                    </span>
                  ) : null}
                </span>
              </FieldLabel>
              <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                <div ref={symbolAnchor} className="tw:min-w-40 tw:flex-1">
                  <Combobox
                    items={options}
                    value={sideSelected ? options[0] : option}
                    onValueChange={(next) => {
                      choose((next?.value ?? '') as UscgSymbol | '');
                    }}
                    itemToStringLabel={(item) => item.label}
                    isItemEqualToValue={(left, right) =>
                      left.value === right.value
                    }
                    onOpenChange={(open, eventDetails) => {
                      if (
                        open &&
                        eventDetails.reason === 'trigger-press' &&
                        blockTriggerOpenRef.current
                      ) {
                        blockTriggerOpenRef.current = false;
                        eventDetails.cancel();
                        return;
                      }
                      menuOpenRef.current = open;
                    }}
                  >
                    <ComboboxTrigger
                      id={`${name}-symbology`}
                      aria-label="Select Symbol"
                      aria-required={required || undefined}
                      className="tw:flex tw:h-9 tw:w-full tw:items-center tw:justify-between tw:gap-1.5 tw:rounded-lg tw:border tw:border-input tw:bg-transparent tw:px-2.5 tw:text-sm tw:shadow-xs"
                    >
                      <ComboboxValue placeholder="None" />
                    </ComboboxTrigger>
                    <ComboboxContent anchor={symbolAnchor} className="dark">
                      <ComboboxInput
                        placeholder="Search symbols"
                        showTrigger={false}
                        aria-label="Search symbols"
                      />
                      <ComboboxEmpty>No symbols match.</ComboboxEmpty>
                      <ComboboxList>
                        {(item: SymbologyOptionType) => (
                          <ComboboxItem key={item.value || 'none'} value={item}>
                            <span className="tw:flex tw:items-center tw:gap-2">
                              {item.swatch ? (
                                <span
                                  aria-hidden
                                  className="tw:inline-block tw:size-3 tw:shrink-0 tw:rounded-full"
                                  style={{ background: item.swatch }}
                                />
                              ) : null}
                              {item.iconSrc ? (
                                <img
                                  src={item.iconSrc}
                                  alt=""
                                  className="symbology-icon"
                                />
                              ) : null}
                              <span>{item.label}</span>
                            </span>
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>
                </div>
                {showSideSymbols ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger
                      render={
                        <Button
                          type="button"
                          size="sm"
                          variant={sideSelected ? 'default' : 'outline'}
                        >
                          + Add Custom Symbol
                        </Button>
                      }
                    />
                    <DropdownMenuContent align="start" className="dark">
                      <DropdownMenuItem onClick={() => choose('customPoint')}>
                        Custom Point
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => choose('customLine')}>
                        Custom Line
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => choose('customPolygon')}>
                        Custom Polygon
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>
            </Field>
          );
        }

        return (
          <PratusSelect
            label="Symbology"
            options={options}
            value={option}
            onChange={(opt) => {
              if (!opt || !('value' in opt) || typeof opt.value !== 'string') {
                return;
              }
              const newValue = opt.value as UscgSymbol;
              onChange(newValue);
              onSymbologyChange?.(newValue || null);
            }}
            formatOptionLabel={(data) =>
              formatSymbologyOptionLabel(data as SymbologyOptionType)
            }
          />
        );
      }}
    />
    </div>
  );
};

export default FormSymbologySelect;
